import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as ChevronDownIcon } from '../assets/ic_chevron_down.svg';
import classNames from 'classnames';

interface IDropdownMenuProps<T> {
  buttonClassName?: string;
  iconClassName?: string;
  label: string;
  labelClassName?: string;
  items: T[];
  renderItem: (item: T) => JSX.Element;
  onItemSelect: (item: T) => void;
}

const DropdownMenu = <T,>({
  buttonClassName,
  iconClassName,
  label,
  labelClassName,
  items,
  renderItem,
  onItemSelect,
}: IDropdownMenuProps<T>) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleToggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleItemClick = (item: T) => {
    onItemSelect(item);
    setShowMenu(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block text-left" ref={menuRef}>
      <button
        className={classNames(
          'flex gap-2 justify-between items-center px-3 py-1 text-black rounded-2xl border border-solid border-stone-300 hover:bg-gray-100',
          buttonClassName,
        )}
        onClick={handleToggleMenu}
      >
        <div className={labelClassName}>{label}</div>
        <ChevronDownIcon
          className={classNames(
            'shrink-0 my-auto w-4 aspect-[2] stroke-[2px] stroke-neutral-400',
            iconClassName,
          )}
        />
      </button>

      {showMenu && (
        <div className="origin-top-right absolute left-0 mt-1 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1">
            {items.map((item, index) => (
              <div
                key={`DDM_${index}`}
                className="flex items-center px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100"
                onClick={() => {
                  handleItemClick(item);
                }}
              >
                {renderItem(item)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
